import { getWawechratData } from "api/graphQL_dashboard";
import LineChart from "components/charts/LineChart";
import { useEffect, useState, useContext, useCallback } from "react";
import { lineChartOptionsTotalSpent as lineChartOptionsTotalSpentinit } from "variables/charts";
import { ClientContext } from "ClientProvider";

const STATUS_KEYS = ["APPROVED", "DECLINED", "ERRORED", "FAILED"] as const;
const STATUS_META: Record<string, { color: string }> = {
  APPROVED: { color: "#00c896" },
  DECLINED: { color: "#ff4d6d" },
  ERRORED: { color: "#f59e0b" },
  FAILED: { color: "#6366f1" },
};

const TotalSpent = ({
  datesTop, timeFrameTop, selectOrg, selectClient, selectCard,
}: {
  type?: string; datesTop?: any; timeFrameTop?: any;
  selectOrg?: any; selectClient?: any; selectCard?: any;
}) => {
  const [chartData, setChartData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lineChartData, setLineChartData] = useState<any[]>();
  const [lineChartOptions, setLineChartOptions] = useState<any>();
  const { dashboardData } = useContext(ClientContext);
  const [dates, setDates] = useState<[Date, Date]>(datesTop);
  const [timeFrame, setTimeFrame] = useState<string>(timeFrameTop);

  const getInitialDates = () => {
    const now = new Date();
    const back = new Date(now);
    back.setMonth(back.getMonth() - 3);
    setDates([back, now]);
  };

  const fetchPayments = useCallback(() => {
    setIsLoading(true);
    let dataP: any = {
      time_frame: timeFrame,
      org_ids: selectOrg?.length > 0 ? selectOrg?.map((m: any) => m?.id)?.toString() : "",
      client_ids: selectClient?.length > 0 ? selectClient?.map((m: any) => m?.id)?.toString() : "",
      card_type: selectCard?.length > 0 ? selectCard?.map((m: any) => m?.value)?.toString() : "",
    };
    if (dates) {
      dataP = { ...dataP, start_date: dates[0].toISOString().substring(0, 10), end_date: dates[1].toISOString().substring(0, 10) };
    }
    if (timeFrame !== "custom") { delete dataP.start_date; delete dataP.end_date; }

    getWawechratData(dataP, dashboardData)
      .then((data) => {
        setChartData(data);

        const groupedBy = data?.grouped_by;

        // Union of every period key across all four statuses, so series with
        // sparse data (e.g. FAILED only has one month) still line up correctly.
        const allKeysSet = new Set<string>();
        STATUS_KEYS.forEach((sk) => {
          const obj = data?.[sk] || {};
          Object.keys(obj).forEach((k) => allKeysSet.add(k));
        });
        // "YYYY-MM" sorts correctly lexicographically; for "day" grouping the
        // original key ordering is preserved same as before.
        const sortedKeys = Array.from(allKeysSet).sort();

        const xAxis = sortedKeys.map((k) => {
          const parts = k.split("-");
          const month = groupedBy === "day" ? parts[0] : parts[1];
          const name = new Date(2000, Number(month) - 1).toLocaleString("default", { month: "short" });
          return [name + "-" + (groupedBy === "day" ? parts[1] : parts[0])];
        });

        const series = STATUS_KEYS
          .map((sk) => {
            const obj = data?.[sk] || {};
            if (Object.keys(obj).length === 0) return null;
            return {
              name: sk,
              data: sortedKeys.map((k) => obj[k] ?? 0),
              color: STATUS_META[sk].color,
            };
          })
          .filter(Boolean);

        setLineChartData(series);
        setLineChartOptions({
          ...lineChartOptionsTotalSpentinit,
          xaxis: { ...lineChartOptionsTotalSpentinit.xaxis, categories: xAxis },
        });
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [dashboardData, dates, timeFrame, selectOrg, selectClient, selectCard]);

  useEffect(() => { getInitialDates(); }, []);
  useEffect(() => { const t = setTimeout(fetchPayments, 1000); return () => clearTimeout(t); }, [fetchPayments]);
  useEffect(() => { setDates(datesTop); setTimeFrame(timeFrameTop); }, [datesTop, timeFrameTop]);

  const hasData = chartData?.total_transactions > 0;

  const STATS = [
    { label: "Total",    value: chartData?.total_transactions,    cls: "ts-stat-b", lCls: "ts-stat-lbl-b", vCls: "ts-stat-val-b", sub: "All transactions" },
    { label: "Approved", value: chartData?.transactions_approved, cls: "ts-stat-g", lCls: "ts-stat-lbl-g", vCls: "ts-stat-val-g", sub: "Successful" },
    { label: "Declined", value: chartData?.transactions_declined, cls: "ts-stat-r", lCls: "ts-stat-lbl-r", vCls: "ts-stat-val-r", sub: "Failed" },
    { label: "Errored",  value: chartData?.transactions_errored,  cls: "ts-stat-y", lCls: "ts-stat-lbl-y", vCls: "ts-stat-val-y", sub: "System errors" },
    { label: "Failed",   value: chartData?.transactions_failed,   cls: "ts-stat-i", lCls: "ts-stat-lbl-i", vCls: "ts-stat-val-i", sub: "Processing failed" },
  ];

  return (
    <div className="ts-card">
      {/* ── Decorative ── */}
      <div className="ts-orb" />
      <div className="ts-shine" />

      {/* ── Header ── */}
      <div className="ts-hdr">
        <div className="ts-hl">
          <div className="ts-ico">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
          <div>
            <h3 className="ts-title">Transaction Trends</h3>
            <p className="ts-subtitle">Transaction status over time</p>
          </div>
        </div>
        <div className="ts-badges">
          {hasData && <>
            <div className="ts-badge ts-badge--blue">
              {chartData?.total_transactions?.toLocaleString()} Total
            </div>
            <div className="ts-badge ts-badge--green">
              {chartData?.transactions_approved?.toLocaleString()} Approved
            </div>
            <div className="ts-badge ts-badge--red">
              {chartData?.transactions_declined?.toLocaleString()} Declined
            </div>
            {chartData?.transactions_errored > 0 && (
              <div className="ts-badge ts-badge--amber">
                {chartData?.transactions_errored?.toLocaleString()} Errored
              </div>
            )}
            {chartData?.transactions_failed > 0 && (
              <div className="ts-badge ts-badge--indigo">
                {chartData?.transactions_failed?.toLocaleString()} Failed
              </div>
            )}
          </>}
        </div>
      </div>

      {/* ── Stat strip ── */}
      {/* {hasData && (
        <div className="ts-stats-strip">
          {STATS.map((s) => (
            <div key={s.label} className={`ts-stat-card ${s.cls}`}>
              <div className={`ts-stat-lbl ${s.lCls}`}>{s.label}</div>
              <div className={`ts-stat-val ${s.vCls}`}>{s.value?.toLocaleString() ?? "—"}</div>
              <div className="ts-stat-sub">{s.sub}</div>
            </div>
          ))}
        </div>
      )} */}

      {/* ── Chart body ── */}
      <div className="ts-body">
        {isLoading ? (
          <div className="ts-state">
            <div className="ts-spinner" />
            <p className="ts-hint">Loading chart data…</p>
          </div>
        ) : lineChartOptions && lineChartData && hasData ? (
          <>
            {/* <div className="ts-legend-row">
              <div className="ts-leg-item"><div className="ts-leg-dot" style={{ background: '#00c896' }} />Approved</div>
              <div className="ts-leg-item"><div className="ts-leg-dot" style={{ background: '#ff4d6d' }} />Declined</div>
              <div className="ts-leg-item"><div className="ts-leg-dot" style={{ background: '#f59e0b' }} />Errored</div>
              <div className="ts-leg-item"><div className="ts-leg-dot" style={{ background: '#6366f1' }} />Failed</div>
            </div> */}
            <div className="ts-chart-wrap">
              <LineChart chartOptions={lineChartOptions} chartData={lineChartData} />
            </div>
          </>
        ) : (
          <div className="ts-state">
            <div className="ts-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <p className="ts-empty-title">No transaction data</p>
            <p className="ts-hint">Try selecting a different time frame or date range</p>
          </div>
        )}
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        /* ── Card shell ── */
        .ts-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          border-radius: 28px; overflow: hidden; display: flex; flex-direction: column;
          position: relative; height: 100%;
          background: linear-gradient(155deg,#f5f8ff 0%,#eef2ff 55%,#f8f9ff 100%);
          border: 1.5px solid #d4dbff;
        }

        /* decorative orb */
        .ts-orb {
          position: absolute; top: -70px; right: -70px;
          width: 220px; height: 220px; border-radius: 50%;
          pointer-events: none; opacity: .07;
          background: radial-gradient(circle,#6366f1,transparent);
        }
        /* shimmer sweep */
        .ts-shine {
          position: absolute; inset: 0;
          background: linear-gradient(120deg,transparent 30%,rgba(255,255,255,.5) 50%,transparent 70%);
          animation: tsShine 5s ease-in-out infinite;
          pointer-events: none; border-radius: 28px; z-index: 0;
        }
        @keyframes tsShine { 0%,100%{opacity:0;transform:translateX(-100%)} 50%{opacity:1;transform:translateX(100%)} }

        /* ── Header ── */
        .ts-hdr {
          padding: 16px 20px 14px;
          display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
          border-bottom: 1px solid rgba(99,102,241,.1); position: relative; z-index: 2;
        }
        .ts-hl { display: flex; align-items: center; gap: 11px; }
        .ts-ico {
          width: 42px; height: 42px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center; flex-shrink: 0;
          background: linear-gradient(135deg,#818cf8,#4f46e5);
          box-shadow: 0 6px 20px rgba(99,102,241,.4),inset 0 1.5px 0 rgba(255,255,255,.45);
        }
        .ts-title { font-size: 13px; font-weight: 800; color: #0d1b3e; letter-spacing: -.2px; line-height: 1.3; margin-bottom: 2px; }
        .ts-subtitle { font-size: 9.5px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; color: #6366f1; }

        .ts-badges { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
        .ts-badge { font-size: 11px; font-weight: 900; padding: 5px 13px; border-radius: 100px; white-space: nowrap; }
        .ts-badge--blue   { background: linear-gradient(135deg,#818cf8,#4f46e5); color: #fff; box-shadow: 0 4px 12px rgba(99,102,241,.35); }
        .ts-badge--green  { background: linear-gradient(135deg,#00e5a8,#00b889); color: #003d2a; box-shadow: 0 4px 12px rgba(0,198,150,.3); }
        .ts-badge--red    { background: linear-gradient(135deg,#ff7a87,#e04055); color: #fff; box-shadow: 0 4px 12px rgba(214,52,72,.3); }
        .ts-badge--amber  { background: linear-gradient(135deg,#fbbf24,#f59e0b); color: #fff; box-shadow: 0 4px 12px rgba(245,158,11,.3); }
        .ts-badge--indigo { background: linear-gradient(135deg,#818cf8,#4f46e5); color: #fff; box-shadow: 0 4px 12px rgba(99,102,241,.3); }

        /* ── Stat strip ── */
        .ts-stats-strip {
          display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px;
          padding: 14px 18px 2px; position: relative; z-index: 2;
        }
        .ts-stat-card {
          border-radius: 16px; padding: 12px 14px; border: 1.5px solid;
          position: relative; overflow: hidden;
          transition: transform .15s, box-shadow .15s; cursor: default;
        }
        .ts-stat-card:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,.07); }
        .ts-stat-card::after {
          content: ''; position: absolute; top: -20px; right: -20px;
          width: 70px; height: 70px; border-radius: 50%; opacity: .07; pointer-events: none;
        }
        .ts-stat-b { background: linear-gradient(135deg,#f5f7ff,#eef1ff); border-color: #c7d0f8; }
        .ts-stat-b::after { background: #6366f1; }
        .ts-stat-g { background: linear-gradient(135deg,#f0fff8,#e6fff4); border-color: #b2f5db; }
        .ts-stat-g::after { background: #00c896; }
        .ts-stat-r { background: linear-gradient(135deg,#fff5f5,#ffe8e8); border-color: #ffc5c5; }
        .ts-stat-r::after { background: #ff4d6d; }
        .ts-stat-y { background: linear-gradient(135deg,#fffaf0,#fff3d6); border-color: #f9d58a; }
        .ts-stat-y::after { background: #f59e0b; }
        .ts-stat-i { background: linear-gradient(135deg,#f5f7ff,#eceeff); border-color: #c7d0f8; }
        .ts-stat-i::after { background: #6366f1; }

        .ts-stat-lbl { font-size: 9px; font-weight: 800; letter-spacing: .6px; text-transform: uppercase; margin-bottom: 6px; }
        .ts-stat-lbl-b { color: #4f46e5; }
        .ts-stat-lbl-g { color: #00a67c; }
        .ts-stat-lbl-r { color: #d63448; }
        .ts-stat-lbl-y { color: #d97706; }
        .ts-stat-lbl-i { color: #4f46e5; }
        .ts-stat-val { font-size: 22px; font-weight: 900; letter-spacing: -1px; line-height: 1; }
        .ts-stat-val-b { color: #1e1b4b; }
        .ts-stat-val-g { color: #006b4f; }
        .ts-stat-val-r { color: #b01020; }
        .ts-stat-val-y { color: #a16207; }
        .ts-stat-val-i { color: #3730a3; }
        .ts-stat-sub { font-size: 9px; font-weight: 600; margin-top: 4px; opacity: .5; color: #344563; }

        /* ── Chart ── */
        .ts-body { flex: 1; padding: 0px; position: relative; z-index: 2; }
        .ts-legend-row { display: flex; gap: 16px; margin-bottom: 10px; padding: 0 4px; flex-wrap: wrap; }
        .ts-leg-item { display: flex; align-items: center; gap: 6px; font-size: 11px; font-weight: 700; color: #344563; }
        .ts-leg-dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
        .ts-chart-wrap { height: 280px; }

        /* ── States ── */
        .ts-state {
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          min-height: 240px; gap: 10px;
        }
        .ts-empty-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: rgba(255,255,255,.8); border: 1.5px solid rgba(99,102,241,.15);
          display: flex; align-items: center; justify-content: center; color: #a5b4fc;
        }
        .ts-empty-title { font-size: 13px; font-weight: 800; color: #475080; }
        .ts-hint { font-size: 11.5px; color: #8a96b8; font-weight: 500; text-align: center; max-width: 240px; line-height: 1.5; }
        .ts-spinner { width: 28px; height: 28px; border-radius: 50%; border: 2.5px solid #c7d0f8; border-top-color: #4f46e5; animation: tsSpin .7s linear infinite; }
        @keyframes tsSpin { to { transform: rotate(360deg); } }

        @media (max-width: 600px) {
          .ts-stats-strip { grid-template-columns: 1fr 1fr; }
          .ts-badges { gap: 6px; }
          .ts-badge { font-size: 10px; padding: 4px 10px; }
        }
      `}</style>
    </div>
  );
};

export default TotalSpent;